// Static blog content — 4 SEO guides for organic traffic.
export const BLOG_POSTS = [
  {
    slug: "tax-lien-vs-tax-deed-investing",
    title: "Tax Lien vs. Tax Deed Investing: Which Is Right for You?",
    excerpt:
      "Both start with unpaid property taxes, but the risk, returns, and timelines are completely different. Here's how to pick your lane before your first auction.",
    date: "2026-05-12",
    readTime: "7 min read",
    category: "Fundamentals",
    image: "https://images.unsplash.com/photo-1773427640897-98400b5fe05f?w=1200&h=630&fit=crop&q=80",
    sections: [
      {
        p: [
          "Every year, county governments across the United States are owed billions in unpaid property taxes. To recover that money, they run public sales — and depending on the state, what's actually being sold is very different. In roughly half the country you buy a lien (the debt). In the other half you buy the deed (the property itself). Confusing the two is the fastest way to lose money at your first auction.",
        ],
      },
      {
        h: "Tax lien states: you're buying the debt, not the house",
        p: [
          "In lien states like Florida, Arizona, and Illinois, the county sells a tax lien certificate. You pay the delinquent taxes on the owner's behalf, and in exchange you earn a statutory interest rate — often 8% to 18% annually — when the owner redeems (pays you back). If they never redeem within the redemption period, you can begin foreclosure and potentially take the property.",
          "The appeal is predictable, collateralized yield. Most liens redeem. You are effectively making a secured loan where the collateral is real estate worth many multiples of your investment. The catch: your money can be tied up for one to three years, and the bidding process in competitive counties can drive effective returns down sharply.",
        ],
      },
      {
        h: "Tax deed states: you're buying the property at auction",
        p: [
          "In deed states like Texas, California, and Tennessee (through its chancery court sales), the county auctions the property itself after the redemption window has run. Opening bids are typically the back taxes plus penalties and costs — which is why you'll see six-figure properties open at $8,000 or $15,000.",
          "The upside is obvious: deep discounts to market value. The risks are just as real — you usually buy as-is with no interior inspection, title needs to be cleared before resale, and some states still give the former owner a short post-sale redemption right.",
        ],
      },
      {
        h: "Which one fits your goals?",
        list: [
          "Want passive, interest-style returns with lower capital? Start with liens.",
          "Want to own, flip, or rent property at a discount? Focus on deed sales.",
          "Live in a hybrid state (like Georgia's redeemable deeds)? Learn your state's redemption mechanics cold before bidding.",
          "Either way: never bid on a property you haven't researched. The auction is the last step, not the first.",
        ],
      },
      {
        h: "Where the lists come from",
        p: [
          "Both strategies begin the same way — with the county's delinquent or sale list. Counties publish these as PDFs, spreadsheets, courthouse postings, or auction-site listings, each in its own format and on its own schedule. That fragmentation is exactly why we built Lien Scout: we pull the official lists from county treasurers, chancery courts, and tax collectors nationwide, normalize them into one searchable table, and score every record so you can shortlist in minutes instead of weekends.",
        ],
      },
    ],
  },
  {
    slug: "how-to-research-tax-sale-property",
    title: "How to Research a Tax Sale Property Before You Bid (Step-by-Step Checklist)",
    excerpt:
      "The list is public. The profit is in the research. A repeatable 20-minute due-diligence workflow for any parcel on any county's sale list.",
    date: "2026-05-26",
    readTime: "8 min read",
    category: "Due Diligence",
    image: "https://images.unsplash.com/photo-1562564055-71e051d33c19?w=1200&h=630&fit=crop&q=80",
    sections: [
      {
        p: [
          "Tax sale lists are long — Miami-Dade's annual delinquency notice alone runs past 30,000 records. Winners aren't the people who bid the most; they're the people who researched the right 20 parcels while everyone else skimmed the first page. Here's the checklist we recommend running on every candidate before auction day.",
        ],
      },
      {
        h: "1. Verify the parcel on the county assessor's site",
        p: [
          "Take the parcel number (APN) from the sale list and pull it up on the county assessor or property appraiser's website. Confirm the situs address, lot size, improvements, and assessed value. A surprising number of cheap-looking listings are utility strips, drainage easements, or landlocked slivers with no road access.",
        ],
      },
      {
        h: "2. Compare the amount owed to real market value",
        p: [
          "This is the single most important ratio in tax sale investing: the lien-to-value (LTV). A $6,000 tax bill against a $300,000 house is a 2% LTV — the owner will almost certainly redeem, or you're buying massive equity. A $45,000 bill against a $60,000 lot is 75% — walk away. Lien Scout computes this automatically when you attach a live market valuation to any property, and bakes it into the AI Deal Score.",
        ],
      },
      {
        h: "3. Check for senior liens and encumbrances",
        p: [
          "Property tax liens usually prime mortgages, but they don't wipe out everything. IRS liens, municipal demolition liens, code enforcement fines, and utility assessments can survive the sale in some jurisdictions. Search the county recorder's index for the owner's name and the parcel before you bid.",
        ],
      },
      {
        h: "4. Drive by (or street-view) the property",
        p: [
          "Fire damage, unpermitted structures, illegal dumping, and full-scale occupation don't show up in the county's one-line record. If you can't physically visit, use recent street imagery and satellite views — and discount your maximum bid for everything you can't verify.",
        ],
      },
      {
        h: "5. Read the sale terms twice",
        list: [
          "Deposit and payment deadlines — many counties demand full payment within 24–72 hours.",
          "Redemption rights — can the former owner reclaim the property after you buy? For how long, and at what premium?",
          "Title condition — most tax deeds require a quiet-title action before you can resell with title insurance.",
          "Buyer premiums and recording fees that raise your true cost basis.",
        ],
      },
      {
        h: "Make it repeatable",
        p: [
          "Due diligence is a pipeline, not a one-off. Save your target filters — county, price band, sale window, minimum deal score — as a Lien Scout alert, and let new matches come to you by email the moment counties publish. Your research time goes into the best candidates, not into finding them.",
        ],
      },
    ],
  },
  {
    slug: "tax-sale-investing-mistakes",
    title: "The 7 Costliest Mistakes First-Time Tax Sale Investors Make",
    excerpt:
      "Most beginners don't lose at the auction — they lose in the weeks before and after it. Learn the seven traps that turn 'cheap' properties into expensive lessons.",
    date: "2026-06-09",
    readTime: "6 min read",
    category: "Strategy",
    image: "https://images.unsplash.com/photo-1676181739859-08330dea8999?w=1200&h=630&fit=crop&q=80",
    sections: [
      {
        p: [
          "Tax sales attract beginners because the entry price looks low. But the discount exists for a reason: the process is unforgiving, and the county owes you nothing after the gavel falls. These are the seven mistakes we see most often — every one of them avoidable.",
        ],
      },
      {
        h: "1. Bidding on unseen land",
        p: [
          "The $2,000 'residential lot' that turns out to be a retention pond is a cliché in this business because it happens constantly. If you haven't at least street-viewed the parcel and checked the plat map, you're gambling, not investing.",
        ],
      },
      {
        h: "2. Ignoring the lien-to-value ratio",
        p: [
          "Beginners sort by 'cheapest first.' Professionals sort by lien-to-value. A tiny tax bill on a valuable property is the entire game — it means either a near-certain redemption payoff or a deep-equity acquisition. Chasing absolute cheapness instead of relative value is how you end up owning a $900 sliver of swamp.",
        ],
      },
      {
        h: "3. Blowing the payment deadline",
        p: [
          "Win a bid and miss the county's 24- or 48-hour payment window, and you don't just lose the deal — many counties keep your deposit and can bar you from future sales. Have certified funds ready before you raise your hand.",
        ],
      },
      {
        h: "4. Assuming the sale wipes every lien",
        p: [
          "Federal tax liens, municipal fines, and special assessments can survive in many jurisdictions. One recorder's-office search costs you an hour. Skipping it can cost you the entire property's value in surprise debt.",
        ],
      },
      {
        h: "5. Forgetting the redemption period",
        p: [
          "In redeemable-deed and lien states, the former owner can reclaim the property months or years after you 'bought' it. Budget as if redemption will happen, treat the premium as your return, and never renovate a property that can still be redeemed out from under you.",
        ],
      },
      {
        h: "6. Skipping quiet title",
        p: [
          "A tax deed is not marketable title. Until you complete a quiet-title action (or buy specialized title certification), you can't sell with title insurance — which means you can't sell to most financed buyers. Price the legal cost into every bid.",
        ],
      },
      {
        h: "7. Researching only one county",
        p: [
          "Sale calendars are staggered all year across 3,000+ counties. Investors who only watch their home county see a dozen opportunities a year and force marginal deals. Widening to a whole state — or the top metros in several — turns tax sales into a steady pipeline. That's exactly what Lien Scout's nationwide coverage and saved-search email alerts are built for: more counties, zero extra hours.",
        ],
      },
    ],
  },
  {
    slug: "lien-to-value-ratio-explained",
    title: "Lien-to-Value: The One Number That Separates Deals From Duds",
    excerpt:
      "Amount owed means nothing by itself. Divide it by real market value and every tax sale list snaps into focus. Here's how to use LTV — and how Lien Scout automates it.",
    date: "2026-06-23",
    readTime: "5 min read",
    category: "Deal Analysis",
    image: "https://images.unsplash.com/photo-1707623988408-ab88c9981730?w=1200&h=630&fit=crop&q=80",
    sections: [
      {
        p: [
          "Scan any delinquent tax list and your eye goes straight to the amount owed column. It's the wrong column. A $30,000 balance might be the best deal on the page and a $1,500 balance might be the worst — you can't know until you compare each against what the property is actually worth. That comparison is the lien-to-value ratio: amount owed ÷ market value.",
        ],
      },
      {
        h: "Reading the ratio",
        list: [
          "Under 5% LTV — exceptional. Massive equity cushion; owners redeem fast in lien states, and deed buyers acquire deep discounts.",
          "5–15% — strong. This is the working range where most professional tax sale investors live.",
          "15–30% — situational. Worth research if the property or location is compelling.",
          "30–60% — thin. The 'discount' is mostly gone after fees, title work, and repairs.",
          "Over 60% — usually a pass. You're often paying near-retail for an as-is, no-inspection property.",
        ],
      },
      {
        h: "The hard part is the denominator",
        p: [
          "Counties tell you the amount owed to the penny — but they don't tell you market value. Assessed values lag the market by years and are systematically low. Manually pulling comps for hundreds of parcels is the bottleneck that keeps most investors stuck in one county.",
          "Lien Scout solves this with on-demand valuations: open any property, click Get Market Value, and we pull a live automated valuation (with a value range and comparable count), compute the lien-to-value ratio instantly, and fold it into the property's AI Deal Score as an equity factor. Properties under 15% LTV get flagged as hot deals — in the app and in your alert emails.",
        ],
      },
      {
        h: "Turn it into a system",
        p: [
          "Ratios are most powerful as filters, not one-off calculations. Save a search for your target metros, set a minimum deal score, and let Lien Scout email you when a new listing clears your bar. The counties keep publishing. The system keeps ranking. You just show up for the shortlist.",
        ],
      },
    ],
  },
];

export const getPost = (slug) => BLOG_POSTS.find((p) => p.slug === slug);
